import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSettings, saveSettings } from "@/lib/schedule.functions";
import { getMe } from "@/lib/core.functions";
import { listAccessRules, addAccessRule, removeAccessRule } from "@/lib/access.functions";

import { PageHeader, EmptyState } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({
    meta: [
      { title: "Connections | Leadership Edge Scheduling" },
      { name: "description", content: "Endpoints for the website course catalog, Training Pipeline, and calendar invites." },
      { property: "og:title", content: "Connections | Leadership Edge Scheduling" },
      { property: "og:description", content: "Website, Training Pipeline, and calendar connection settings." },
    ],
  }),
  component: SettingsPage,
});

interface Form {
  website_endpoint_url: string;
  training_pipeline_url: string;
  calendar_from_address: string;
  mock_mode: boolean;
}

function SettingsPage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchSettings = useServerFn(getSettings);
  const save = useServerFn(saveSettings);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({ queryKey: ["settings"], queryFn: () => fetchSettings() });

  const [form, setForm] = useState<Form>({
    website_endpoint_url: "",
    training_pipeline_url: "",
    calendar_from_address: "",
    mock_mode: true,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      website_endpoint_url: data.website_endpoint_url ?? "",
      training_pipeline_url: data.training_pipeline_url ?? "",
      calendar_from_address: data.calendar_from_address ?? "",
      mock_mode: data.mock_mode ?? true,
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      save({
        data: {
          website_endpoint_url: form.website_endpoint_url.trim() ? form.website_endpoint_url.trim() : null,
          training_pipeline_url: form.training_pipeline_url.trim() ? form.training_pipeline_url.trim() : null,
          calendar_from_address: form.calendar_from_address.trim() ? form.calendar_from_address.trim() : null,
          mock_mode: form.mock_mode,
        },
      }),
    onSuccess: () => {
      toast.success("Connections saved.");
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!me?.isAdmin) {
    return (
      <>
        <PageHeader eyebrow="Integrations" title="Connections" />
        <EmptyState title="Admins only" body="Connection settings are managed by administrators." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Integrations"
        title="Connections"
        description="Deploy pushes to these targets. With simulation on, every push is recorded in the deploy log without leaving the tool."
      />

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="panel max-w-2xl space-y-5 p-6">
          <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-surface px-4 py-3">
            <div>
              <Label htmlFor="mock" className="cursor-pointer">
                Simulation mode
              </Label>
              <p className="mt-1 text-sm text-muted-foreground">
                Record every push in the deploy log instead of calling the live services.
              </p>
            </div>
            <Switch id="mock" checked={form.mock_mode} onCheckedChange={(checked) => setForm({ ...form, mock_mode: checked })} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="website">Website course catalog endpoint</Label>
            <Input
              id="website"
              value={form.website_endpoint_url}
              onChange={(e) => setForm({ ...form, website_endpoint_url: e.target.value })}
              placeholder="https://leadershipedge.example.com/api/course-schedule"
            />
            <p className="text-xs text-muted-foreground">
              Receives the full session list for the timeframe so each course page can render its dates.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pipeline">Training Pipeline base URL</Label>
            <Input
              id="pipeline"
              value={form.training_pipeline_url}
              onChange={(e) => setForm({ ...form, training_pipeline_url: e.target.value })}
              placeholder="https://api.trainingpipeline.example.com/classes"
            />
            <p className="text-xs text-muted-foreground">
              Used to create class sessions on deploy and to pull enrollment counts.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="calendar">Calendar sender address</Label>
            <Input
              id="calendar"
              type="email"
              value={form.calendar_from_address}
              onChange={(e) => setForm({ ...form, calendar_from_address: e.target.value })}
              placeholder="scheduling@leadershipedge.example.com"
            />
            <p className="text-xs text-muted-foreground">Trainer invites are sent from this mailbox.</p>
          </div>

          <Button variant="brass" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            Save connections
          </Button>
        </div>
      )}

      <AccessRules />
    </>
  );
}

function AccessRules() {
  const queryClient = useQueryClient();
  const fetchRules = useServerFn(listAccessRules);
  const addRule = useServerFn(addAccessRule);
  const removeRule = useServerFn(removeAccessRule);
  const [kind, setKind] = useState<"domain" | "email">("email");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");

  const { data } = useQuery({ queryKey: ["access-rules"], queryFn: () => fetchRules() });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["access-rules"] });

  const add = useMutation({
    mutationFn: () => addRule({ data: { kind, value, note: note.trim() ? note.trim() : null } }),
    onSuccess: () => {
      setValue("");
      setNote("");
      toast.success("Access rule saved.");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (input: { kind: "domain" | "email"; value: string }) => removeRule({ data: input }),
    onSuccess: () => {
      toast.success("Access rule removed.");
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="panel mt-8 max-w-2xl space-y-5 p-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Who can sign in</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Only approved domains and listed email exceptions can reach the console. Everyone else is signed out at the
          door.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Approved domains</Label>
        {(data?.domains ?? []).map((d) => (
          <div key={d.domain} className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
            <span className="text-sm text-foreground">@{d.domain}</span>
            <Button variant="ghost" size="sm" onClick={() => remove.mutate({ kind: "domain", value: d.domain })}>
              Remove
            </Button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <Label>Email exceptions</Label>
        {(data?.emails ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No exceptions yet.</p>
        ) : (
          (data?.emails ?? []).map((e) => (
            <div key={e.email} className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2">
              <span className="text-sm text-foreground">
                {e.email}
                {e.note ? <span className="ml-2 text-xs text-muted-foreground">{e.note}</span> : null}
              </span>
              <Button variant="ghost" size="sm" onClick={() => remove.mutate({ kind: "email", value: e.email })}>
                Remove
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="rule-kind">Type</Label>
          <select
            id="rule-kind"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as "domain" | "email")}
          >
            <option value="email">Email exception</option>
            <option value="domain">Domain</option>
          </select>
        </div>
        <div className="min-w-[14rem] flex-1 space-y-1.5">
          <Label htmlFor="rule-value">{kind === "domain" ? "Domain" : "Email address"}</Label>
          <Input
            id="rule-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === "domain" ? "example.com" : "person@example.com"}
          />
        </div>
        <div className="min-w-[12rem] flex-1 space-y-1.5">
          <Label htmlFor="rule-note">Note (optional)</Label>
          <Input id="rule-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Contract trainer" />
        </div>
        <Button variant="brass" onClick={() => add.mutate()} disabled={add.isPending || value.trim().length < 3}>
          Add
        </Button>
      </div>
    </div>
  );
}

